import { IAugmentedJQuery, IComponentOptions, IController, ITranscludeFunction, IWindowService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { actionHandlerGenerator } from "@gtmhub/shared/utils";
import { LEGACY_TOP_NAV_BAR_PROPS } from "@webapp/navigation/components/navigation/config";
import { setTopNavBarProps } from "@webapp/top-nav-bar/utils/top-nav-bar.utils";
import { ItemAction } from "../models";

export interface ISubTitle {
  text: string;
  action: ItemAction;
}

interface INavTitleOptions {
  setMaxWidth: boolean;
}

/**
 * @example <top-nav-bar nav-title=settings subtitle=`{text: '${lists}', state: 'gtmhub.lists' }` search-facet="employees">
 *             <top-nav-bar-title-editable>
 *              (In case if we want inline edit on title)
 *                <editable-textarea class="inline-height-22" ng-model="..." ng-change="...">
 *                </editable-textarea>
 *             </top-nav-bar-title-editable>
 *
 *             <top-nav-bar-title-actions>
 *              (Can be component or actions/buttons)
 *             </top-nav-bar-title-actions>
 *
 *             <top-nav-bar-actions>
 *                <a ui-sref="gtmhub.configuration" ng-class="{'current': isConfigurationState }">Configuration</a>
 *                <a data-test-id="usersSettings" ui-sref="gtmhub.usersList" ui-sref-active="current"><span class="gh-font-icon-users-group"></span>Users</a>
 *             </top-nav-bar-actions>
 *
 *              <top-nav-bar-actions>
 *                (Can be component for contributors/permissions or buttons for actions)
 *                <button gh-button size="small" kind="secondary" ui-sref="gtmhub.roles">Manage roles</button>
 *                <button gh-button size="small" kind="primary" ui-sref="gtmhub.usersList._invitation.invite" ng-if="featureAvailable('people.invite')">Invite more people</button>
 *              </top-nav-bar-actions>
 *           </top-nav-bar>
 */
class TopNavBarComponentCtrl implements IController {
  // bindings
  navTitle: string; // Title text can be dynamic variable -> list.name / session.title / whiteboard.name
  navTitleOptions: INavTitleOptions;
  subtitle: ISubTitle;
  searchFacet: string;
  itemsFiltered?: string;
  focusSubtitle: boolean;

  transcludeSlotsFilled: {
    employee: boolean;
    titleEditable: boolean;
    titleActions: boolean;
    favoriteButton: boolean;
    tabs: boolean;
    actions: boolean;
    teamDetails: boolean;
    titleAvatar: boolean;
  };
  actionHandler: () => unknown;

  static $inject = ["$transclude", "$state", "$window", "$element", "NewHomeExperienceService"];

  constructor(private $transclude: ITranscludeFunction, private $state: StateService, private $window: IWindowService, $element: IAugmentedJQuery) {
    $element[0].classList.add("legacy");
  }

  $onInit(): void {
    setTopNavBarProps({ height: LEGACY_TOP_NAV_BAR_PROPS.height + "px", borderColor: LEGACY_TOP_NAV_BAR_PROPS.borderColor });

    this.transcludeSlotsFilled = {
      employee: this.$transclude.isSlotFilled("employee"),
      titleEditable: this.$transclude.isSlotFilled("titleEditable"),
      titleActions: this.$transclude.isSlotFilled("titleActions"),
      favoriteButton: this.$transclude.isSlotFilled("favoriteButton"),
      tabs: this.$transclude.isSlotFilled("tabs"),
      actions: this.$transclude.isSlotFilled("actions"),
      teamDetails: this.$transclude.isSlotFilled("teamDetails"),
      titleAvatar: this.$transclude.isSlotFilled("titleAvatar"),
    };

    if (this.subtitle?.action) {
      this.actionHandler = actionHandlerGenerator(this.subtitle.action, { $state: this.$state, $window: this.$window });
    }
  }

  isTooltipEnabled(): boolean {
    const el = document.querySelector(".title-wrapper .title");
    if (!el) {
      return false;
    }

    const elStyles = window.getComputedStyle(el);
    const elWidth = el.clientWidth;
    const elMaxWidth = elStyles.maxWidth || "0px";
    const elMaxWidthNumber = Number(elMaxWidth.slice(0, elMaxWidth.length - 2));

    return elWidth === elMaxWidthNumber;
  }

  onKeypressEnter(event: KeyboardEvent): void {
    if (event.code === "Enter" && this.actionHandler) {
      this.actionHandler();
    }
  }
}

export const TopNavBarComponent: IComponentOptions = {
  /*
      TODO?: template: (el, attr) => ... in order to remove ng-if from view and watches from bindings.
      Also to not render ng-transclude elemets if there is no content
  */
  template: require("./top-nav-bar.html"),
  controller: TopNavBarComponentCtrl,
  transclude: {
    employee: "?topNavBarEmployee",
    titleEditable: "?topNavBarTitleEditable",
    titleActions: "?topNavBarTitleActions",
    favoriteButton: "?topNavBarFavoriteButton",
    tabs: "?topNavBarTabs",
    actions: "?topNavBarActions",
    teamDetails: "?topNavBarTeamDetails",
    titleAvatar: "?topNavBarTitleAvatar",
  },
  bindings: {
    navTitle: "@",
    navTitleOptions: "<",
    subtitle: "<",
    searchFacet: "@",
    itemsFiltered: "@?",
    focusSubtitle: "<",
  },
};
